import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SaveButton } from '../../../components/mui/Buttons/Buttons';
import { createSkillxXpRule, getSkillxXpRules } from '../../../redux/features/skillxSlice';

/**
 * Create a new XP rule.
 *
 * A rule created here is reachable only by manual award: the automatic
 * producers live in the student portal and know their own rule keys, so a new
 * key means nothing to them until one is wired to it. That is why the dialog
 * defaults `allow_manual_award` on — a new rule nobody can award is inert.
 */

const ValidationSchema = Yup.object().shape({
  rule_key: Yup.string()
    .trim()
    .required('Rule key is required')
    .max(40, 'Rule key is too long')
    .matches(
      /^[a-z0-9_]+$/,
      'Lowercase letters, numbers and underscores only — the key appears in URLs and in the ledger',
    ),
  label: Yup.string().trim().required('Label is required').max(120, 'Label is too long'),
  points: Yup.number()
    .typeError('Points must be a number')
    .integer('Points must be a whole number')
    .min(1, 'Points must be at least 1')
    .max(1000, 'That is a very large award — cap a single rule at 1000')
    .required('Points are required'),
  icon: Yup.string().trim().max(120, 'Icon name is too long'),
});

export default function AddXpRule({ dispatchparams }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      rule_key: '',
      label: '',
      points: 5,
      icon: '',
      is_bonus: false,
      is_active: true,
      allow_manual_award: true,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      dispatch(
        createSkillxXpRule({
          rule_key: values.rule_key,
          label: values.label,
          points: Number(values.points),
          icon: values.icon || null,
          is_bonus: values.is_bonus,
          is_active: values.is_active,
          allow_manual_award: values.allow_manual_award,
        }),
      ).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getSkillxXpRules(dispatchparams));
          formik.resetForm();
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Failed to create the rule', {
            variant: 'error',
          });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit, setFieldValue } = formik;

  return (
    <GlobalDialog
      title="Add XP Rule"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Add XP Rule
        </AddButton>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info" sx={{ fontSize: 12.5 }}>
                A new rule can only be given by a mentor from <b>Award XP</b>. The automatic
                producers (login, attendance, assignments) award their own fixed keys — wiring
                a new one to them is a backend change.
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  name="label"
                  label="Activity name"
                  fullWidth
                  value={values.label}
                  onChange={(e) => {
                    handleChange(e);
                    // Suggest a key from the label until the user types their
                    // own — the key is the part that has to be machine-safe,
                    // and it is not the part anyone wants to think about.
                    if (!touched.rule_key) {
                      setFieldValue(
                        'rule_key',
                        e.target.value
                          .toLowerCase()
                          .trim()
                          .replace(/[^a-z0-9]+/g, '_')
                          .replace(/^_+|_+$/g, '')
                          .slice(0, 40),
                      );
                    }
                  }}
                  error={touched.label && Boolean(errors.label)}
                  helperText={touched.label && errors.label}
                />
                <TextField
                  name="points"
                  label="Points"
                  type="number"
                  fullWidth
                  value={values.points}
                  onChange={handleChange}
                  error={touched.points && Boolean(errors.points)}
                  helperText={touched.points && errors.points}
                />
              </Stack>

              <TextField
                name="rule_key"
                label="Rule key"
                fullWidth
                value={values.rule_key}
                onChange={handleChange}
                error={touched.rule_key && Boolean(errors.rule_key)}
                helperText={
                  (touched.rule_key && errors.rule_key) ||
                  'Permanent. Every XP event stores this key, so it can never be renamed afterwards.'
                }
              />

              <Box>
                <TextField
                  name="icon"
                  label="Icon (optional)"
                  fullWidth
                  value={values.icon}
                  onChange={handleChange}
                  error={touched.icon && Boolean(errors.icon)}
                  helperText={
                    (touched.icon && errors.icon) ||
                    'An Iconify name, e.g. solar:star-shine-bold-duotone'
                  }
                />
                {/* The field wants a name from a set of ~200,000 — the only
                    practical way to find one is to search Iconify itself. */}
                <Box sx={{ pl: 1.75, pt: 0.25 }}>
                  <Chip
                    size="small"
                    clickable
                    component="a"
                    href="https://icon-sets.iconify.design/"
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<Icon icon="solar:magnifer-bold-duotone" width={13} height={13} />}
                    label="Browse icons on Iconify"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.allow_manual_award}
                      onChange={(e) => setFieldValue('allow_manual_award', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5 }}>
                      Mentors may award this by hand
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.is_bonus}
                      onChange={(e) => setFieldValue('is_bonus', e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5 }}>
                      Bonus — stacks on top of another award
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.is_active}
                      onChange={(e) => setFieldValue('is_active', e.target.checked)}
                    />
                  }
                  label={<Typography sx={{ fontSize: 13.5 }}>Active</Typography>}
                />
              </Box>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
                <SaveButton disabled={loading} size="small" type="submit">
                  {loading ? 'Please wait...' : 'Create'}
                </SaveButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}
